import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/auth-context';
import './login-page.css';

function LoginPage() {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const planParam       = searchParams.get('plan'); // set when redirected from pricing page
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, user } = useAuth();

  const [mode, setMode]         = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);

  // Already signed in — bounce to dashboard
  if (user) {
    navigate(planParam ? '/pricing?upgrade=' + planParam : '/dashboard', { replace: true });
    return null;
  }

  async function handleOAuth(providerFn) {
    setError('');
    setBusy(true);
    try {
      await providerFn();
      navigate(planParam ? '/pricing?upgrade=' + planParam : '/dashboard', { replace: true });
    } catch (err) {
      setError(friendlyError(err.message));
    } finally {
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate(planParam ? '/pricing?upgrade=' + planParam : '/dashboard', { replace: true });
    } catch (err) {
      setError(friendlyError(err.message));
    } finally {
      setBusy(false);
    }
  }

  function friendlyError(msg) {
    if (msg.includes('user-not-found') || msg.includes('wrong-password') || msg.includes('invalid-credential')) {
      return 'Incorrect email or password.';
    }
    if (msg.includes('email-already-in-use')) return 'An account with that email already exists.';
    if (msg.includes('weak-password')) return 'Password must be at least 6 characters.';
    if (msg.includes('popup-closed')) return 'Sign-in popup was closed. Please try again.';
    if (msg.includes('operation-not-allowed')) return 'This sign-in method isn\'t enabled yet. Try email/password, or contact support.';
    if (msg.includes('unauthorized-domain')) return 'This site isn\'t authorized for sign-in yet. Contact support.';
    return msg;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src="/logo.svg" alt="CryptoProScanner" className="login-logo-img" />
          <p className="login-tagline">Real-time signals. Smarter trades.</p>
        </div>

        <div className="login-oauth">
          <button
            className="oauth-btn google-btn"
            onClick={() => handleOAuth(signInWithGoogle)}
            disabled={busy}
          >
            <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div className="login-divider">
          <span>or</span>
        </div>

        <form className="login-form" onSubmit={handleEmailSubmit}>
          <input
            type="email"
            className="login-input"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={busy}
          />
          <input
            type="password"
            className="login-input"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            disabled={busy}
          />
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-submit-btn" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div className="login-toggle">
          {mode === 'signin' ? (
            <span>No account? <button className="toggle-link" onClick={() => setMode('signup')}>Sign up free</button></span>
          ) : (
            <span>Have an account? <button className="toggle-link" onClick={() => setMode('signin')}>Sign in</button></span>
          )}
        </div>

        <p className="login-back">
          <button className="toggle-link" onClick={() => navigate('/')}>&#8592; Back to home</button>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
